import './userById.css'
import { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import DashboardLayout from 'components/layout/dashboard/DashboardLayout'
import AuthContext from 'contexte/AuthContext'
import { useTranslation } from 'react-i18next';
import { fetchUserById, fetchFeaturesById, fetchFraudCheck, uploadUserRiskLevel, fetchUserDocuments } from 'services/fetchUserById'
import PageLoader from 'components/pageLoader/PageLoader'
import { RELEASE_DATE, formatDate } from 'utils/date';
import UserDataArray from './UserDataArray/UserDataArray'
import { launchError, launchSuccess } from 'components/notifications'
import Notification from 'components/notifications/Notification'
import { formatNumberWithSpaces, formatPhoneNumber, getDecimalValue } from 'utils/numbers'
import { addFreeTransactionsToUser, closeAccount, exportPortfolioHistory, muteUser, reducedMarginFeesToUser, reduceUserOvernightMarginFees, setUserReferral, unmuteUser, updateCertification, updateMarginProviderAccess, updatePartnerStatus, checkCloseAccount } from 'services/userServices'
import DateRangeSelector from 'components/Filters/DateSelector/DateSelector'
import { downloadFile } from 'utils/helpers'
import Modal from 'components/modal/Modal'
import { useNavigate } from 'react-router-dom';
import SearchUser from 'components/SearchUser/SearchUser'
import { searchUsers } from 'services/fetchUsers'
import UserDocuments from './UserSections/UserDocuments/UserDocuments'
import UserFeatures from './UserSections/UserFeatures/UserFeatures'
import UserPartner from './UserSections/UserPartner/UserPartner'
import UserReferrals from './UserSections/UserReferrals/UserReferrals'
import UserSales from './UserSections/UserSales/UserSales'
import UserMarginFees from './UserSections/UserMarginFees/UserMarginFees'
import { COMPANY_DOC_TYPES, USER_DOC_TYPES } from 'utils/types'
import UserGoliathsPlus from './UserSections/UserGoliathsPlus/UserGoliathsPlus'
import UserSignalProvider from './UserSections/UserSignalProvider/UserSignalProvider'
import UserEditInfos from './UserSections/UserEditInfos/UserEditInfos'
import UserRbaMatrix from './UserSections/UserRbaMatrix/UserRbaMatrix'
import UserSwissNotice from './UserSections/UserSwissNotice/UserSwissNotice'
import UserBonuses from './UserSections/UserBonuses/UserBonuses'

const UserById = () => {


  let { id } = useParams()
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user, verifyUser } = useContext(AuthContext)
  const role = verifyUser()?.role;


  const [loading, setLoading] = useState(true)
  const [userData, setUserData] = useState({});
  const [features, setFeatures] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFreeTransactions, setNewFreeTransactions] = useState(0);
  const [fraudCheck, setFraudCheck] = useState({ devices: 0, cards: 0 })
  const [riskLevel, setRiskLevel] = useState(userData?.risk_level);
  const [userDocuments, setUserDocuments] = useState([]);
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const [partnerPercentages, setPartnerPercentages] = useState({
    stocks: 0,
    leverage: 0,
    structured_products: 0
  });

  const [marginFeeQuantity, setMarginFeeQuantity] = useState(0);
  const [marginFeePercentage, setMarginFeePercentage] = useState(0);

  const [exportStartingDate, setExportStartingDate] = useState({
    date1: { startDate: RELEASE_DATE },
  });
  const [exportEndingDate, setExportEndingDate] = useState({
    date1: { startDate: new Date() },
  });
  const [marginOfferEndingDate, setMarginOfferEndingDate] = useState({
    date1: { startDate: new Date() },
  });

  const [overnightFeesDiscount, setOvernightFeesDiscount] = useState(0);
  const [overnightFeesDiscountEndingDate, setOvernightFeesDiscountEndingDate] = useState({
    date1: { startDate: new Date() },
  });

  const [referrals, setReferrals] = useState([]);
  const [selectedReferral, setSelectedReferral] = useState({})

  const filterDocuments = (documents, allowedTypes) => {
    if (!documents) return {};
    return Object.fromEntries(Object.entries(documents).filter(([key]) => allowedTypes.includes(key)))
  }


  const fetchData = async () => {
    try {
      const getUserData = await fetchUserById(user, id)
      setUserData(getUserData)
      setRiskLevel(getUserData.risk_level);
    } catch {
      console.error('error fetch data')
    } finally {
      setLoading(false)
    }
  }

  const getFraudCheck = async () => {
    try {
      const data = await fetchFraudCheck(user, id)
      setFraudCheck(data)
    } catch (error) {
      launchError(t('notifications.users.userById.fraud_check.error'))
    }
  }

  const getUserDocuments = async () => {
    try {
      const data = await fetchUserDocuments(user, id)
      setUserDocuments(filterDocuments(data, USER_DOC_TYPES));
      setCompanyDocuments(filterDocuments(data, COMPANY_DOC_TYPES));
    } catch (error) {
      launchError(t('notifications.users.userById.user_documents.error'))
    }
  }

  const fetchFeatures = async () => {
    try {
      if (role === 1) {
        const getUserFeatures = await fetchFeaturesById(user, id);
        setFeatures(getUserFeatures);
      }
    } catch (error) {
      console.error(error)
    }
  }

  const changePartner = async () => {
    try {
      const data = await updatePartnerStatus(user, id, !userData.is_partner);
      setUserData(prev => ({ ...prev, is_partner: data.is_partner, partner_percentages: data.partner_percentages }));
      setPartnerPercentages({
        stocks: 0,
        leverage: 0,
        structured_products: 0
      })
      launchSuccess(t('notifications.users.userById.partner.success'))
    } catch (error) {
      launchError(t('notifications.users.userById.partner.error'))
    }
  }


  const changeCertification = async () => {
    try {
      await updateCertification(user, id, !userData.isCertified);
      setUserData(prev => ({ ...prev, isCertified: !prev.isCertified }))
      launchSuccess(t('notifications.users.userById.certification.success'))
    } catch (error) {
      launchError(t('notifications.users.userById.certification.error'))
    }
  }

  const updateMute = async () => {
    try {
      if (userData.is_mute)
        await unmuteUser(user, id);
      else
        await muteUser(user, id);
      setUserData(prev => ({ ...prev, is_mute: !prev.is_mute }));
      launchSuccess(t('notifications.users.userById.mute.success'))
    } catch (error) {
      launchError(t('notifications.users.userById.mute.error'))
    }
  }

  useEffect(() => {
    fetchData()
    if ([1, 2].includes(role)) {
      getFraudCheck()
      fetchFeatures()
    }
    if ([1, 2, 3, 4].includes(role)) {
      getUserDocuments()
    }
  }, [])

  const addFreeTransactions = async () => {
    try {
      const remaining = await addFreeTransactionsToUser(user, id, newFreeTransactions)
      setUserData(prev => {
        const newUserData = { ...prev };
        newUserData['free_transactions']['remaining'] = remaining;
        return newUserData;
      })
      setNewFreeTransactions(0);
      launchSuccess(t('notifications.users.userById.update.success'));
    } catch (error) {
      launchError(t('notifications.users.userById.update.error'));

    }
  }

  const addMarginReducedFees = async () => {
    try {
      if (marginFeePercentage && marginFeeQuantity && marginOfferEndingDate.date1) {
        const result = await reducedMarginFeesToUser(user, id, {
          quantity: marginFeeQuantity,
          percentage: marginFeePercentage,
          endDate: marginOfferEndingDate.date1?.startDate,
        })
        setUserData(prev => {
          const newUserData = { ...prev };
          newUserData['reduced_margin_fees'] = result;
          return newUserData;
        })
        setMarginFeePercentage(0)
        setMarginFeeQuantity(0)
        setMarginOfferEndingDate({
          date1: { startDate: new Date() },
        })
        launchSuccess(t('notifications.users.userById.update.success'));
      }
    } catch (error) {
      launchError(t('notifications.users.userById.update.error'));

    }
  }

  const reduceOvernightMarginFees = async () => {
    try {
      if (overnightFeesDiscount && overnightFeesDiscount <= 50 && overnightFeesDiscountEndingDate.date1) {
        const result = await reduceUserOvernightMarginFees(user, id, {
          discount: overnightFeesDiscount,
          endDate: overnightFeesDiscountEndingDate.date1?.startDate,
        })
        setUserData(prev => {
          const newUserData = { ...prev };
          newUserData['overnight_fees_discount'] = result;
          return newUserData;
        })
        setOvernightFeesDiscount(0)
        setOvernightFeesDiscountEndingDate({
          date1: { startDate: new Date() },
        })
        launchSuccess(t('notifications.users.userById.update.success'));
      }
    } catch (error) {
      console.error(error)
      launchError(t('notifications.users.userById.update.error'));

    }
  }

  const exportHistory = async () => {
    try {
      const data = await exportPortfolioHistory(user, id, exportStartingDate.date1.startDate, exportEndingDate.date1.startDate);
      downloadFile({ data, type: 'excel', filename: `history_report_${id}.xlsx` })
    } catch (e) {
      console.error(e)
      launchError('Erreur download link')
    }
  }

  const updateRiskLevel = async () => {
    try {
      const new_risk = await uploadUserRiskLevel(user, id, riskLevel);
      setUserData(prev => ({ ...prev, risk_level: new_risk }))
      launchSuccess(t('notifications.users.userById.risk_level.success'))
    } catch (error) {
      launchError(t('notifications.users.userById.risk_level.error'))
    }
  }

  const updateProviderAccess = async () => {
    try {
      const has_lmax_access = await updateMarginProviderAccess(user, id, !userData.leverage?.has_lmax_access);
      const newUserData = { ...userData };
      newUserData['leverage']['has_lmax_access'] = has_lmax_access;
      setUserData(newUserData)
      launchSuccess(t('notifications.users.userById.margin_provider.success'))
    } catch (error) {
      console.error(error)
      launchError(t('notifications.users.userById.margin_provider.error'))
    }
  }

  const closeUserAccount = async (user_uuid, reason) => {
    try {
      setLoading(true);
      setIsModalOpen(false);
      await closeAccount(user, user_uuid, reason);
      launchSuccess(t('notifications.users.userById.close_alpaca_account.success'))
      navigate('/users/list');
    } catch (error) {
      console.log(error)
      const reasons = error?.response?.data?.reasons || [];
      if (reasons.length > 0)
        launchError(t(`notifications.users.userById.close_alpaca_account.check_close_account_error`, { reasons: reasons.join(', ') }))
      else
        launchError(t(`notifications.users.userById.close_alpaca_account.error`))
    } finally {
      setLoading(false);
    }
  }

  const handleAmount = (field, event) => {
    const value = event.target.value;
    if (!value || value.match(/^\d{1,}(\.\d{0,4})?$/)) {
      switch (field) {
        case 'free_transactions':
          setNewFreeTransactions(Number(value));
          break;
        case 'margin_fees_percentage':
          setMarginFeePercentage(Number(value));
          break;
        case 'margin_fees_quantity':
          setMarginFeeQuantity(Number(value));
          break;
        case 'overnight_fees_discount':
          setOvernightFeesDiscount(Number(value));
          break;
        default:
          break;
      }
    }
  };

  const handleSearchReferrals = async (search) => {
    if (search.length > 0) {
      try {
        const fetchUsers = await searchUsers(user, search)
        setReferrals(fetchUsers)
      } catch (e) {
        console.error(e)
      }
    } else {
      setReferrals([])
    }
  }

  const addReferral = async () => {
    try {
      if (selectedReferral.uuid) {
        const referrer = await setUserReferral(user, id, selectedReferral.uuid);
        setUserData({ ...userData, referral_uuid: selectedReferral.uuid, referrer });
        setSelectedReferral({});
        launchSuccess(t('notifications.users.userById.add_referral.success'))
      }
    } catch (error) {
      launchError(t('notifications.users.userById.add_referral.error'))
    }
  }

  return (
    <DashboardLayout page={`${t("pages_name.users")} / ${id}`}>
      <div className=''>
        <Notification />
        {
          !loading ? (
            <div className='userById_container'>
              <div className='userData_header'>
                <div className='userData_profil'>
                  <img src={`${process.env.REACT_APP_CDN_URL}/usr/pic/${userData.pic}`}
                    alt="profilepic"
                    className='user_profile_pic' />
                  <div>
                    <p className='user_profil_name'>{userData.firstName} {userData.lastName} {userData.company && role === 1 && `(${userData.company})`}
                      {userData.isCertified &&
                        <span>
                          <img src="/img/certified.svg" alt="certified" />
                        </span>
                      }
                      {userData.is_deleted && ' (Compte supprimé)'}
                    </p>
                    <p className='user_profil_email'>{userData.email}</p>
                  </div>
                </div>
                <div className='user_header_action_button_box'>
                  {role === 1 &&
                    <>
                      <button className='userData_action_button' onClick={() => changePartner()}>{userData.is_partner ? 'Retirer statut Partner' : 'Donner statut Partner'}</button>
                      <button className='userData_action_button' onClick={() => changeCertification()}>{userData.isCertified ? 'Dé-certifier' : 'Certifier'}</button>
                      <button className='userData_action_button' onClick={() => updateMute()}>{userData.is_mute ? 'Débloqué' : 'Bloqué'}</button>
                      {userData.alpaca_status === 'APPROVED' && <button className='close_alpaca_account_button' onClick={() => setIsModalOpen(true)}>Fermer le compte</button>}
                    </>
                  }
                </div>
              </div>
              {role === 1 && <UserEditInfos userData={userData} setUserData={setUserData} user={user} id={id} />}
              <div className='userData_multi_box'>
                <div className='userData_kyc'>
                  <p className='user_doc_title'>KYC :</p>
                  <div className='userData_kyc_box'>
                    <div className='userData_registerinfo userData_box'>
                      <p className='userData_registerinfo_created userData_p userData_item_desc'><span className='userData_item_title'>Inscription :</span> {formatDate({ date: userData.created_at, time: true })}</p>
                      {
                        userData.sumsub_status === "VERIFIED" && (
                          <p className='userData_registerinfo_approved userData_p userData_item_desc'><span className='userData_item_title'>Vérifié :</span> {formatDate({ date: userData.review_date, time: true })}</p>
                        )
                      }
                      <p className='userData_registerinfo_created userData_p userData_item_desc'><span className='userData_item_title'>Niveau de risque :</span>
                        <span className='update_risk_level_box'>
                          <select className='custom-select' value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
                            <option value='low'>Low</option>
                            <option value='medium'>Medium</option>
                            <option value='high'>High</option>
                          </select>
                          <button onClick={updateRiskLevel} className='user_action_button' disabled={riskLevel === userData.risk_level}>Modifier</button>
                        </span>
                      </p>
                    </div>
                    <div className='userData_statusinfo userData_box'>
                      <p className='userData_statusinfo_approved userData_p userData_item_desc'><span className='userData_item_title'>Sumsub :</span>{userData.sumsub_status}</p>
                      <p className='userData_statusinfo_created userData_p userData_item_desc'><span className='userData_item_title'>Alpaca :</span>{userData.alpaca_status}</p>
                    </div>
                  </div>
                </div>
                {role === 1 && <div className='userData_fraud'>
                  <p className='user_doc_title'>Fraud check :</p>
                  <div className='userData_fraud_box'>
                    <div className='userData_box'>
                      <label className='label_title'>Nombre de devices utilisés : <span className='label_value'>{fraudCheck.devices}</span></label>
                      <label className='label_title'>Device multi-accounts : <span className={`label_value ${fraudCheck.is_device_multi_account ? 'multi_' : 'mono_'}account_device`}>{fraudCheck.is_device_multi_account ? 'Oui' : 'Non'}</span></label>
                      <label className='label_title'>Somme des dépôts éffectués : <span className='label_value'>{fraudCheck.deposits_sum} €</span></label>
                      <label className='label_title'>Somme des retraits éffectués : <span className='label_value'>{fraudCheck.withdraws_sum} €</span></label>
                    </div>
                    <div className='userData_box'>
                      <label className='label_title'>Nombre de CB enregistrées : <span className='label_value'>{fraudCheck.saved_cards}</span></label>
                      <label className='label_title'>Nombre de CB utilisées (5 dernière transactions): <span className='label_value'>{fraudCheck.cards_used_last_transactions_count}</span></label>
                      <label className='label_title'>Nombre de paiements par CB échoués: <span className='label_value'>{fraudCheck.failed_card_transactions_count}</span></label>
                    </div>

                  </div>
                </div>}
              </div>

              <div className='userData_multi_box'>
                <div className='userData_multi_box_container'>
                  <p className='user_doc_title'>User :</p>
                  <div className='user_row_space_between_box'>
                    <div className='userData_box'>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Numero de téléphone :</span>{formatPhoneNumber(userData.phone)}</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Date de naissance :</span> {formatDate({ date: userData.birthDate })}</p>
                      <p className="userData_p userData_item_desc userData_adresse"><span className='userData_item_title'>Adresse :</span>{userData.address}</p>
                    </div>
                    <div className='userData_box'>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Revenus :</span>{userData.income_amount}</p>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Source :</span>{userData.funding_source}</p>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Industrie :</span>{userData.industry}</p>
                    </div>
                  </div>
                </div>
                <div className='userData_multi_box_container'>
                  <p className='user_doc_title'>Referral :</p>
                  <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Code de parrainage : </span>{userData.referral_code}</p>
                  <p></p>
                  <div className='user_row_space_between_box'>
                    {userData.referral_uuid ?
                      <>
                        <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Prénom : </span>{userData.referrer?.firstName}</p>
                        <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Nom : </span>{userData.referrer?.lastName}</p>
                        <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Email : </span>{userData.referrer?.email}</p>
                      </>
                      :
                      <>
                        <SearchUser
                          id='search_user_input'
                          selectUser={setSelectedReferral}
                          selectedUser={selectedReferral}
                          users={referrals}
                          handleSearch={handleSearchReferrals}
                          label={'Rechercher un parrain'}
                        />
                        <button className='userData_action_button' onClick={addReferral} disabled={!selectedReferral.uuid}>Ajouter</button>
                      </>
                    }
                  </div>
                </div>
              </div>

              {role === 1 && <UserReferrals userData={userData} setUserData={setUserData} user={user} id={id} />}

              {role === 1 && <UserSales userData={userData} setUserData={setUserData} user={user} id={id} />}

              <div className='userData_multi_box'>
                <div className='userData_multi_box_container'>
                  <p className='user_doc_title'>Rapports :</p>
                  <div className='user_row_space_between_box'>
                    <div className='userData_box'>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Valeur totale du portfolio : </span>{formatNumberWithSpaces(userData.portfolioValue)} €</p>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Total déposé : </span>{formatNumberWithSpaces(userData.total_deposited)} €</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Montant total de volume trade :</span>{formatNumberWithSpaces(userData.total_volume)} €</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Montant total d'argent :</span>{formatNumberWithSpaces(userData.wallet)} €</p>
                    </div>
                    <div className='userData_box'>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Nombre total de transactions effectuées : </span>{formatNumberWithSpaces(userData.total_trades)}</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Panier moyen de l'utilisateur : </span>{formatNumberWithSpaces(getDecimalValue(userData.average_invest))} €</p>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Profit sur client (trade + margin + spread) :</span>{formatNumberWithSpaces(userData.profit)} €</p>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Playlists débloquées :</span>{userData.playlist_unlocks.count} ({formatNumberWithSpaces(userData.playlist_unlocks.expenses)} €)</p>
                    </div>
                    <div className='userData_box'>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Gains realisés : </span>{formatNumberWithSpaces(getDecimalValue(userData.realized_pl))} €</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Montant gagné via parrainage : </span>{formatNumberWithSpaces(userData.gift_referral)} €</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Montant gagné via playlist : </span>{formatNumberWithSpaces(userData.gift_playlist)} €</p>
                    </div>
                  </div>
                </div>
                <div className='userData_multi_box_container'>
                  <p className='user_doc_title'>Leverage :</p>
                  <div className='user_row_space_between_box'>
                    <div className='userData_box'>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Volume :</span>{formatNumberWithSpaces(userData.leverage?.volume)} €</p>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Montant emprunté :</span>{formatNumberWithSpaces(userData.leverage?.borrowed_amount)} €</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Profit/Loss :</span> {formatNumberWithSpaces(userData.leverage?.profit_loss)} €</p>
                    </div>
                    <div className='userData_box'>
                      <p className='userData_p userData_item_desc'><span className='userData_item_title'>Trades réalises :</span>{formatNumberWithSpaces(userData.leverage?.trades_count)}</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Commissions :</span> {formatNumberWithSpaces(userData.leverage?.leverage_profit)} €</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Spread :</span> {formatNumberWithSpaces(userData.leverage?.spread)} €</p>
                      <p className="userData_p userData_item_desc"><span className='userData_item_title'>Profit sur client (commissions + spread) :</span> {formatNumberWithSpaces(userData.leverage?.commissions_spread)} €</p>
                    </div>
                  </div>
                </div>
              </div>

              <UserGoliathsPlus userData={userData} setUserData={setUserData} user={user} id={id} />

              {role === 1 && <div className='userData_case'>
                <p className='user_doc_title'>Free transactions :</p>
                <div className='user_row_space_between_box'>
                  <div className='userData_box'>
                    <p className='userData_p userData_item_desc'>
                      <label htmlFor='free_transactions_input' className='userData_item_title'>Nombre de transactions à offrir : </label>
                      <span className='userData_free_transactions_update_box'>
                        <input
                          id='free_transactions_input'
                          type='text'
                          value={newFreeTransactions}
                          className='userData_free_transactions_input'
                          onChange={(e) => { handleAmount('free_transactions', e) }}
                        />
                        <button className='userData_action_button' onClick={addFreeTransactions}>Ajouter</button>
                      </span>
                    </p>
                  </div>
                  {/* <div className='userData_box'> */}
                  <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Transactions offertes : </span>{userData.free_transactions.remaining}</p>
                  {/* </div> */}
                  <div className='userData_box'>
                    <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Transactions offertes en cours :</span>{userData.free_transactions.pending}</p>
                  </div>
                </div>
              </div>}

              {(role === 1 && userData?.is_partner) &&
                <UserPartner user={user} id={id} userData={userData} setUserData={setUserData} />
              }

              {role === 1 &&
                <UserSignalProvider user={user} id={id} userData={userData} setUserData={setUserData} />
              }

              {role === 1 && <div className='userData_case'>
                <p className='user_doc_title'>Margin transactions :</p>
                {userData.reduced_margin_fees?.endDate &&
                  <div className='user_row_space_between_box'>
                    <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Transactions restantes : </span>{userData.reduced_margin_fees?.remaining || 0}</p>
                    <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Date de fin actuelle : </span>{userData.reduced_margin_fees?.endDate ? formatDate({ date: userData.reduced_margin_fees?.endDate, month: 'long' }) : ''}</p>
                    <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Réduction actuelle : </span>{userData.reduced_margin_fees?.percentage || 0} %</p>
                    <p className='userData_p userData_item_desc'></p>
                  </div>
                }
                <div className='user_row_space_between_box'>
                  <div className='userData_box'>
                    <p className='userData_p userData_item_desc'>
                      <label htmlFor='free_transactions_input' className='userData_item_title'>Nombre de transactions tarif réduit : </label>
                      <span className='userData_free_transactions_update_box'>
                        <input
                          id='free_transactions_input'
                          type='number'
                          value={marginFeeQuantity}
                          className='userData_free_transactions_input'
                          onChange={(e) => handleAmount('margin_fees_quantity', e)}
                        />
                      </span>
                    </p>
                  </div>

                  <div className='userData_box'>
                    {/* <p className='userData_p userData_item_desc'> */}
                    <div className='userData_p userData_item_desc'>
                      <label htmlFor='free_transactions_input' className='userData_item_title'>Date de fin : </label>
                      <span className='userData_free_transactions_update_box'>
                        <DateRangeSelector
                          selectedDate={marginOfferEndingDate}
                          setSelectedDate={setMarginOfferEndingDate}
                          simpleDate
                        />
                      </span>
                    </div>
                    {/* </p> */}
                  </div>

                  <div className='userData_box'>
                    <p className='userData_p userData_item_desc'>
                      <label htmlFor='free_transactions_input' className='userData_item_title'>% de réduction : </label>
                      <span className='userData_free_transactions_update_box'>
                        <input
                          id='free_transactions_input'
                          type='number'
                          value={marginFeePercentage}
                          className='userData_free_transactions_input'
                          onChange={(e) => handleAmount('margin_fees_percentage', e)}
                        />
                      </span>
                    </p>
                  </div>

                  <button className='userData_action_button' onClick={addMarginReducedFees}>Ajouter</button>

                </div>
              </div>}

              {role === 1 && <UserMarginFees user={user} userData={userData} setUserData={setUserData} id={id} />}

              {[1, 2].includes(role) && <UserBonuses user={user} id={id} />}

              {role === 1 &&
                <div className='userData_case'>
                  <p className='user_doc_title'>Réduction frais overnight transactions :</p>
                  {userData.overnight_fees_discount?.endDate &&
                    <div className='user_row_space_between_box'>
                      <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Réduction actuelle : </span>{userData.overnight_fees_discount?.discount || 0} %</p>
                      <p className='userData_p userData_item_desc'><span className='userData_free_transactions_title'>Date de fin actuelle : </span>{userData.overnight_fees_discount?.endDate ? formatDate({ date: userData.overnight_fees_discount?.endDate, month: 'long' }) : ''}</p>
                      <p className='userData_p userData_item_desc'></p>
                    </div>
                  }
                  <div className='user_row_space_between_box'>
                    <div className='userData_box'>
                      <p className='userData_p userData_item_desc'>
                        <label htmlFor='free_transactions_input' className='userData_item_title'>% de réduction : </label>
                        <span className='userData_free_transactions_update_box'>
                          <input
                            id='free_transactions_input'
                            type='number'
                            value={overnightFeesDiscount}
                            className='userData_free_transactions_input'
                            onChange={(e) => handleAmount('overnight_fees_discount', e)}
                          />
                        </span>
                      </p>
                    </div>

                    <div className='userData_box'>
                      {/* <p className='userData_p userData_item_desc'> */}
                      <div className='userData_p userData_item_desc'>
                        <label htmlFor='free_transactions_input' className='userData_item_title'>Date de fin : </label>
                        <span className='userData_free_transactions_update_box'>
                          <DateRangeSelector
                            selectedDate={overnightFeesDiscountEndingDate}
                            setSelectedDate={setOvernightFeesDiscountEndingDate}
                            simpleDate
                          />
                        </span>
                      </div>
                      {/* </p> */}
                    </div>

                    <button className='userData_action_button' onClick={reduceOvernightMarginFees}>Ajouter</button>

                  </div>
                </div>}

              {role === 1 && <UserFeatures features={features} userData={userData} setFeatures={setFeatures} setUserData={setUserData} user={user} id={id} />}

              {[1, 2, 3, 4].includes(role) && <UserDocuments key={'user_documents'} documents={userDocuments} setDocuments={setUserDocuments} user={user} id={id} />}

              {role === 1 && <UserDocuments key={'company_documents'} isCompanyDocuments={true} title='Company Documents' documents={companyDocuments} setDocuments={setCompanyDocuments} user={user} id={id} />}

              {[1, 2, 3].includes(role) && <UserSwissNotice user={user} id={id} />}

              {[1, 2, 3, 4].includes(role) && <div className='userData_case'>
                <p className='user_doc_title'>Compliance Export Portfolio History :</p>
                <div className='history_export_box'>
                  <div className='history_export_box_item'>
                    <label htmlFor='export_starting_input' className='userData_item_title'>Starting Date : </label>
                    <DateRangeSelector
                      id='export_starting_input'
                      selectedDate={exportStartingDate}
                      setSelectedDate={setExportStartingDate}
                      simpleDate
                    />
                  </div>
                  <div className='history_export_box_item'>
                    <label htmlFor='export_ending_input' className='userData_item_title'>Ending Date : </label>
                    <DateRangeSelector
                      id='export_ending_input'
                      selectedDate={exportEndingDate}
                      setSelectedDate={setExportEndingDate}
                      simpleDate
                    />
                  </div>
                  <div className='history_export_box_item'>
                    <button className='user_action_button' onClick={exportHistory}>Export</button>
                  </div>
                </div>
              </div>}

              <UserRbaMatrix userData={userData} />

              <UserDataArray id={id} />
              <ModalCloseAccount
                isModalOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                accountToClose={userData}
                closeAccount={closeUserAccount}

              />
            </div>
          ) : (
            <div className='pageLoaderBox'>
              <PageLoader />
            </div>
          )
        }
      </div>
    </DashboardLayout >
  )
}

const ModalCloseAccount = ({ isModalOpen, onClose, accountToClose, closeAccount }) => {

  const { t } = useTranslation()

  const { firstName, lastName, uuid, alpaca_id } = accountToClose;

  const [reason, setReason] = useState('');

  const close = () => {
    closeAccount(uuid, reason);
    setReason('')
  }

  return (
    <Modal isOpen={isModalOpen} onClose={onClose} closeButton >
      <h2 className='modal-playlist_title'>Clôturer le compte Alpaca</h2>
      <div className="close_alpaca_account_modal_box">


        <div className='warning_deleted'>
          <img src="https://assets-global.website-files.com/612cdb668b06dbb2a51f99ee/651e6e6668d310d564622c79_warning_img.png" alt="" width={25} />
          <span>Êtes-vous sûr de vouloir clôturer le compte de l'utilisateur <strong>{firstName} {lastName}</strong> ?
          </span>
        </div>
        <select className="custom-select close_alpaca_account_modal_reason_select" onChange={(e) => setReason(e.target.value)} value={reason}>
          <option value={''}>Choisissez une raison</option>
          <option value={'fee'}>Frais trop élevés</option>
          <option value={'complexity'}>L'application est trop compliquée</option>
          <option value={'lost'}>L'utilisateur a perdu de l'argent</option>
          <option value={'better'}>L'utilisateur a trouvé une meilleure app</option>
          <option value={'invest'}>L'utilisateur ne souhaite plus investir</option>
          <option value={'other'}>Autre</option>
        </select>
      </div>
      <hr className='modal_hr_button' />
      <div className='close_alpaca_account_modal_button_div'>
        <button className='close_alpaca_account_close_modal_button' onClick={onClose}>{t("pages.payouts.buttons.close")}</button>
        <button className='close_alpaca_account_validate_button' onClick={close} disabled={!reason}>Clôturer</button>
      </div>
    </Modal>

  )
}

export default UserById